import { useMemo, useCallback } from 'react'
import { useDrawer } from '@core/context/DrawerContext'
import { useBreakpoints } from '@/core/hooks/useBreakpoints/useBreakpoints'

export type TabType = 'all' | 'incidents' | 'service-requests'

interface TimeRange {
    id: string
    label: string
}

interface Incident {
    caseNumber: string
    title: string
    severity: string
    status: string
    createdAt: string
    lastModifiedAt: string
}

interface ServiceRequest {
    caseNumber: string
    title: string
    status: string
    createdAt: string
    lastModifiedAt: string
}

interface AllCases {
    incidents: Incident[]
    serviceRequests: ServiceRequest[]
}

interface FormattedCase {
    id: string
    type: 'Incident' | 'ServiceRequest'
    title: string
    severity: TCaseSeverity
    status: string
    lastModified: string
}

interface TabDef {
    id: TabType
    label: string
    count: number
}

interface UseOpenCasesResult {
    filteredCases: FormattedCase[]
    formattedCases: FormattedCase[]
    tabs: TabDef[]
    openDrawer: (caseItem: FormattedCase) => void
}

export function useOpenCases(
    allCases: AllCases,
    timeRange: TimeRange,
    activeTab: TabType
): UseOpenCasesResult {
    const { setDrawerState } = useDrawer()
    const { belowBreakpoint } = useBreakpoints()

    const getCutoffDate = (days: string) => {
        const filterDays = parseInt(days)
        const cutoffDate = new Date()
        cutoffDate.setDate(cutoffDate.getDate() - filterDays)
        return cutoffDate
    }

    const cases = useMemo(() => {
        if (timeRange.id === 'all') return allCases
        const cutoffDate = getCutoffDate(timeRange.id)
        return {
            incidents: allCases.incidents.filter(
                (incident) => new Date(incident.createdAt) >= cutoffDate
            ),
            serviceRequests: allCases.serviceRequests.filter(
                (sr) => new Date(sr.createdAt) >= cutoffDate
            ),
        }
    }, [allCases, timeRange])

    const formattedCases = useMemo<FormattedCase[]>(() => {
        const openIncidents = cases.incidents
            .filter((incident) => incident.status !== 'closed')
            .map((incident) => ({
                id: incident.caseNumber,
                type: 'Incident' as const,
                title: incident.title,
                severity: incident.severity.toLowerCase() as TCaseSeverity,
                status:
                    incident.status === 'inprogress'
                        ? 'inProgress'
                        : incident.status,
                lastModified: new Date(
                    incident.lastModifiedAt
                ).toLocaleDateString(),
            }))
        const openServiceRequests = cases.serviceRequests
            .filter((sr) => sr.status !== 'closed')
            .map((sr) => ({
                id: sr.caseNumber,
                type: 'ServiceRequest' as const,
                title: sr.title,
                severity: 'medium' as TCaseSeverity,
                status: sr.status === 'inprogress' ? 'inProgress' : sr.status,
                lastModified: new Date(sr.lastModifiedAt).toLocaleDateString(),
            }))

        return [...openIncidents, ...openServiceRequests]
    }, [cases])

    const filteredCases = useMemo(() => {
        if (activeTab === 'all') return formattedCases
        return formattedCases.filter((c) =>
            activeTab === 'incidents'
                ? c.type === 'Incident'
                : c.type === 'ServiceRequest'
        )
    }, [activeTab, formattedCases])

    const tabs = useMemo<TabDef[]>(
        () => [
            { id: 'all', label: 'All', count: formattedCases.length },
            {
                id: 'incidents',
                label: 'Incidents',
                count: formattedCases.filter((c) => c.type === 'Incident')
                    .length,
            },
            {
                id: 'service-requests',
                label: 'Service Requests',
                count: formattedCases.filter((c) => c.type === 'ServiceRequest')
                    .length,
            },
        ],
        [formattedCases]
    )

    const openDrawer = useCallback(
        (caseItem: FormattedCase) => {
            const isIncident = caseItem.type === 'Incident'
            const drawerContentType = isIncident
                ? 'caseDetails'
                : 'serviceRequestDetails'

            // Get the original case data from the appropriate array
            const originalCases = isIncident
                ? allCases.incidents
                : allCases.serviceRequests
            const originalCase = originalCases.find(
                (c) => c.caseNumber === caseItem.id
            )

            if (!originalCase) return

            const sameTypeCases = isIncident
                ? allCases.incidents.filter((c) => c.status !== 'closed')
                : allCases.serviceRequests.filter((c) => c.status !== 'closed')
            const currentIndex = sameTypeCases.findIndex(
                (c) => c.caseNumber === caseItem.id
            )

            setDrawerState((prevState: any) => ({
                ...prevState,
                isDrawerOpen: true,
                drawerContentType,
                contentProps: {
                    width: belowBreakpoint('xl')
                        ? '73vw'
                        : belowBreakpoint('xxl')
                          ? '68vw'
                          : '55vw',
                    numberOfItems: sameTypeCases.length,
                    currentIndex,
                    cases: sameTypeCases,
                    currentTab: isIncident ? 'incidents' : 'serviceRequests',
                    withNavigation: true,
                },
            }))
        },
        [
            allCases.incidents,
            allCases.serviceRequests,
            setDrawerState,
            belowBreakpoint,
        ]
    )

    return { filteredCases, formattedCases, tabs, openDrawer }
}
